import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Download, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "sally_install_dismissed_at";
// Re-show the banner after 48h even if the user dismissed it previously.
const DISMISS_TTL_MS = 48 * 60 * 60 * 1000;

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /android/i.test(navigator.userAgent || "");
}

function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)");
  return (mm && mm.matches) || window.navigator.standalone === true;
}

/**
 * InstallAppButton — persistently shown on Android browsers on the login page so the
 * user can install the PWA. Uses the native `beforeinstallprompt` event when the
 * browser fires it (Chrome / Edge / Samsung Internet). Falls back to a helper
 * modal with visual instructions for browsers that don't support it (Firefox).
 */
export default function InstallAppButton() {
  const location = useLocation();
  const onLogin = location.pathname === "/login" || location.pathname === "/";

  const android = useMemo(() => isAndroid(), []);
  const standalone = useMemo(() => isStandalonePWA(), []);

  const [nativePromptReady, setNativePromptReady] = useState(!!window.deferredInstallPrompt);
  const [dismissed, setDismissed] = useState(() => {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw) || 0;
    return Date.now() - ts < DISMISS_TTL_MS;
  });
  const [showHelp, setShowHelp] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const onAvail = () => setNativePromptReady(true);
    const onInstalled = () => setNativePromptReady(false);
    window.addEventListener("sally-install-available", onAvail);
    window.addEventListener("sally-installed", onInstalled);
    return () => {
      window.removeEventListener("sally-install-available", onAvail);
      window.removeEventListener("sally-installed", onInstalled);
    };
  }, []);

  // Show ONLY on Android when the app is not already installed, on the login screen,
  // and while the user hasn't dismissed it in the last 48h.
  const visible = android && !standalone && onLogin && !dismissed;
  if (!visible) return null;

  const doInstall = async () => {
    const p = window.deferredInstallPrompt;
    if (p && typeof p.prompt === "function") {
      setInstalling(true);
      try {
        await p.prompt();
        await p.userChoice;
        window.deferredInstallPrompt = null;
        setNativePromptReady(false);
      } catch (e) {
        console.debug("install prompt aborted", e);
      } finally {
        setInstalling(false);
      }
    } else {
      // Fallback: show manual instructions.
      setShowHelp(true);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <>
      <div
        className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-4 sm:max-w-sm z-[60] bg-slate-950 text-white rounded-sm shadow-2xl border-2 border-blue-600 p-4"
        data-testid="install-prompt"
        role="dialog"
        aria-label="Instalar Banca POS"
      >
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 text-slate-400 hover:text-white"
          data-testid="install-dismiss"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-5">
          <div className="w-10 h-10 bg-blue-600 rounded-sm flex items-center justify-center shrink-0">
            <Download className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-300">
              Instala Banca POS
            </div>
            <div className="font-black text-sm tracking-tight" style={{ fontFamily: "Manrope" }}>
              Vende más rápido desde tu celular
            </div>
            <button
              onClick={doInstall}
              disabled={installing}
              className="mt-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm"
              data-testid="install-accept"
            >
              {installing ? "Instalando…" : nativePromptReady ? "Instalar ahora" : "Cómo instalar"}
            </button>
          </div>
        </div>
      </div>

      {showHelp && (
        <div
          className="fixed inset-0 z-[70] bg-black/70 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowHelp(false)}
          data-testid="install-help-overlay"
        >
          <div
            className="bg-white text-slate-950 w-full sm:max-w-sm rounded-sm border border-slate-200 shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-blue-600 rounded-sm flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-white" />
              </div>
              <div className="font-black tracking-tight" style={{ fontFamily: "Manrope" }}>
                Instalar Banca POS
              </div>
            </div>
            <ol className="text-sm space-y-2 text-slate-700 list-decimal list-inside">
              <li>Toca el menú <b>⋮</b> de tu navegador (arriba a la derecha).</li>
              <li>
                Selecciona <b>Instalar app</b> o <b>Añadir a pantalla de inicio</b>.
              </li>
              <li>Confirma <b>Instalar</b>. El ícono aparecerá en tu pantalla.</li>
            </ol>
            <p className="mt-3 text-xs text-slate-500">
              Si no ves la opción, prueba abrir <span className="font-mono">bancapos.com</span> en <b>Chrome</b> o <b>Edge</b>.
            </p>
            <button
              onClick={() => setShowHelp(false)}
              className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-sm py-2 text-sm"
              data-testid="install-help-close"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
}
